import type { ReactNode } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { useMe } from "@/auth/useMe";
import { SuppressButton } from "@/components/SuppressButton";
import { useAgents } from "@/leads/queries";
import { CallButton } from "@/telephony/CallButton";
import { Avatar } from "./Avatar";
import type { Conversation } from "./queries";
import { useAssign, useCloseConversation, useLinkConversation } from "./queries";

/**
 * Attach an unknown sender to a client, without leaving the thread.
 *
 * Deliberately in the flow rather than on a separate screen (.docs/04 §7): the
 * moment someone knows who this is, is the moment they are reading what the
 * person wrote. Making them navigate away to record it is how conversations
 * stay unlinked forever and the Recepción queue never empties.
 */
function LinkForm({ conversation }: { conversation: Conversation }) {
  const { t } = useTranslation();
  const [company, setCompany] = useState("");
  const link = useLinkConversation();

  return (
    <form
      className="space-y-2"
      onSubmit={(event) => {
        event.preventDefault();
        const id = Number(company);
        if (Number.isInteger(id) && id > 0) {
          link.mutate({ id: conversation.id, company: id });
        }
      }}
    >
      <label className="block text-xs text-wa-meta">
        {t("inbox.linkLabel")}
        <input
          value={company}
          onChange={(event) => setCompany(event.target.value)}
          inputMode="numeric"
          placeholder={t("inbox.linkPlaceholder")}
          className="input mt-1"
        />
      </label>
      {link.isError ? (
        <p className="text-xs text-wa-danger">{(link.error as Error).message}</p>
      ) : null}
      <button
        type="submit"
        disabled={!company || link.isPending}
        className="btn btn-primary btn-block justify-center"
      >
        {t("inbox.link")}
      </button>
    </form>
  );
}

/** A white block of the drawer, the way WhatsApp's contact info stacks them. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-white px-6 py-4">
      <h3 className="pb-2 text-sm text-wa-meta">{title}</h3>
      {children}
    </section>
  );
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="border-b border-wa-line py-2.5 last:border-0">
      <dt className="text-sm text-wa-meta">{label}</dt>
      <dd className="mt-0.5 text-[15px] break-words">{children}</dd>
    </div>
  );
}

/**
 * What *Opciones* opens beside the thread: who this is, and what can be done
 * about them.
 */
export function ClientPanel({ conversation }: { conversation: Conversation }) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const assign = useAssign();
  const close = useCloseConversation();

  const isMine = conversation.agent === (me?.id ?? null);
  // The same roles the server checks before it will move a thread: Recepción
  // works the unassigned queue, management takes what should be theirs.
  const canAssign = (me?.can_triage ?? false) || (me?.is_manager ?? false);
  // `/api/users/agents/` is the one endpoint where a signed-in user sees other
  // people, and it 403s for anyone who may not hand work over.
  const agents = useAgents(canAssign);

  function ownerName(): string {
    if (conversation.agent === null) return t("inbox.unassignedOwner");
    if (isMine) return t("inbox.assignedToYou");
    const named = (agents.data ?? []).find((row) => row.id === conversation.agent);
    return named?.name || `#${conversation.agent}`;
  }

  return (
    <div className="space-y-2.5 pb-4">
      <section className="flex flex-col items-center bg-white px-6 pt-7 pb-5 text-center">
        <Avatar size={120} />
        <h3 className="mt-4 text-xl break-words">
          {conversation.profile_name || conversation.wa_id}
        </h3>
        <p className="text-[15px] text-wa-meta">{conversation.wa_id}</p>
      </section>

      <Section title={t("inbox.client")}>
        <dl>
          <Detail label={t("inbox.company")}>
            {conversation.company ? (
              <Link
                className="hover:underline"
                to={`/prospectos/${conversation.company}`}
              >
                {t("inbox.openCompany")}
              </Link>
            ) : (
              <span className="text-brand-700">{t("inbox.unknownSender")}</span>
            )}
          </Detail>
          <Detail label={t("inbox.agent")}>{ownerName()}</Detail>
          <Detail label={t("inbox.contact")}>
            {conversation.contact ? (
              <Link
                className="hover:underline"
                to={`/contactos/${conversation.contact}`}
              >
                {t("inbox.openContact")}
              </Link>
            ) : (
              "—"
            )}
          </Detail>
        </dl>
      </Section>

      <Section title={t("inbox.actions")}>
        <div className="flex flex-wrap items-center gap-2">
          {/* No `ContactPhone` behind a WhatsApp thread, so this dials the
              `wa_id` as a hand-typed number. It still lands on the company's
              timeline when the conversation is linked, because `start_call`
              resolves the company from the contact, not from the digits. */}
          <CallButton
            number={conversation.wa_id}
            label={conversation.profile_name || conversation.wa_id}
          />

          {/* "No contactar" said in a thread is the same request as said on a
              call (.docs/06 §4). The dial button beside it stays visible after
              — there is no `ContactPhone` here to carry `is_suppressed` — but
              `start_call` refuses the number with a message that says why. */}
          {/* Red, as WhatsApp's own "Bloquear" is. */}
          <SuppressButton
            phone={conversation.wa_id}
            label={t("inbox.doNotContact")}
            className="btn btn-secondary px-3 py-1.5 text-sm text-wa-danger"
          />
        </div>

        {/* An unknown sender lands in Recepción's queue with no agent; this
            is how whoever works that queue picks it up. */}
        {canAssign && !isMine ? (
          <button
            type="button"
            onClick={() =>
              assign.mutate({ id: conversation.id, agent: me?.id ?? null })
            }
            disabled={assign.isPending}
            className="btn btn-secondary btn-block justify-center"
          >
            {t("inbox.assignToMe")}
          </button>
        ) : null}

        {conversation.status !== "closed" ? (
          <button
            type="button"
            onClick={() => close.mutate(conversation.id)}
            disabled={close.isPending}
            className="btn btn-secondary btn-block justify-center"
          >
            {t("inbox.close")}
          </button>
        ) : null}

        {assign.isError ? (
          <p className="mt-2 text-xs text-wa-danger">
            {(assign.error as Error).message}
          </p>
        ) : null}
      </Section>

      {conversation.company === null ? (
        <Section title={t("inbox.linkTitle")}>
          <LinkForm conversation={conversation} />
        </Section>
      ) : null}
    </div>
  );
}
